import { type ElementType, type ReactNode, useEffect, useRef } from 'react';

import { useInView } from '@/hooks/use-in-view';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, SplitText } from '@/lib/gsap';
import { DURATION, GSAP_EASE, STAGGER } from '@/lib/motion-tokens';
import { cn } from '@/lib/utils';

type SplitUnit = 'chars' | 'words' | 'lines';

export interface SplitRevealProps {
  children: ReactNode;
  /** Element to render. Default 'div'. Use a heading tag for headings. */
  as?: ElementType;
  className?: string;
  /** Split granularity. 'lines' reads most editorial; 'chars' is showiest. */
  by?: SplitUnit;
  /** Stagger between units (seconds). */
  stagger?: number;
  duration?: number;
  /** Delay before the reveal starts (seconds). */
  delay?: number;
  /** Fraction of viewport height the top must pass to reveal (default 0.85). */
  threshold?: number;
  /** Reveal only once (default) or replay on scroll back. */
  once?: boolean;
}

/**
 * Masked reveal of text that rises up from behind its own baseline — the house
 * headline entrance. Built on GSAP SplitText with per-line/word/char masking.
 *
 * The split is created after fonts load so line breaks are measured correctly,
 * and fully reverted on unmount. The reveal is driven by the rect-based
 * {@link useInView} hook (not ScrollTrigger, which could strand the text
 * `opacity:0` on a refresh or a jumped-to entry). Under reduced motion the text
 * renders plainly, and if the split ever fails the raw text stays visible.
 */
export function SplitReveal({
  children,
  as: Tag = 'div',
  className,
  by = 'lines',
  stagger = STAGGER.base,
  duration = DURATION.slow,
  delay = 0,
  threshold = 0.85,
  once = true,
}: SplitRevealProps) {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const inView = useInView(ref, { once, threshold, disabled: reduced });

  // Latest props/state read by the async split callback without re-subscribing.
  const unitsRef = useRef<HTMLElement[]>([]);
  const playedRef = useRef(false);
  const revealRef = useRef<() => void>(() => {});
  // Mirror the latest `inView` so the async (fonts.ready) split-creation
  // callback checks the CURRENT value, not the value captured when its effect
  // ran. Without this, a headline in view on load flips `inView` true before
  // the split exists, then the split completes reading a stale `false` — and
  // the text stays hidden forever.
  const inViewRef = useRef(inView);
  inViewRef.current = inView;

  revealRef.current = () => {
    if (reduced || playedRef.current || unitsRef.current.length === 0) return;
    playedRef.current = true;
    gsap.to(unitsRef.current, {
      yPercent: 0,
      opacity: 1,
      duration,
      delay,
      ease: GSAP_EASE.out,
      stagger,
    });
  };

  // Create the split + hidden start state once (after fonts settle).
  useEffect(() => {
    const el = ref.current;
    if (!el || reduced) return;

    let split: SplitText | undefined;
    let ctx: gsap.Context | undefined;
    let cancelled = false;

    const run = () => {
      if (cancelled || !ref.current) return;
      ctx = gsap.context(() => {
        split = SplitText.create(ref.current as HTMLElement, {
          type: by,
          mask: by,
          autoSplit: true,
          linesClass: 'overflow-hidden',
        });
        unitsRef.current = (split[by] as HTMLElement[]) ?? [];
        gsap.set(unitsRef.current, { yPercent: 120, opacity: 0 });
        // If the element is in view now (e.g. an on-load hero headline, whose
        // `inView` flipped true before the split finished building), play now.
        if (inViewRef.current) revealRef.current();
      }, ref);
    };

    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    if (fonts?.ready) {
      fonts.ready.then(run).catch(run);
    } else {
      run();
    }

    return () => {
      cancelled = true;
      playedRef.current = false;
      unitsRef.current = [];
      split?.revert();
      ctx?.revert();
    };
    // Intentionally not keyed on inView: the split is built once; the separate
    // effect below plays the reveal when the element enters view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced, by]);

  // Play the reveal when the element enters view (or immediately if already in).
  useEffect(() => {
    if (inView) revealRef.current();
  }, [inView]);

  return (
    <Tag ref={ref} className={cn(className)}>
      {children}
    </Tag>
  );
}
